function generatePeaksFromChannelData(channelData, targetPeaks) {
    const samplesPerPeak = Math.max(1, Math.floor(channelData.length / targetPeaks));
    const numPeaks = Math.ceil(channelData.length / samplesPerPeak);
    const peaks = new Float32Array(numPeaks);

    for (let i = 0; i < numPeaks; i++) {
        const start = i * samplesPerPeak;
        const end = Math.min(start + samplesPerPeak, channelData.length);
        let maxAbs = 0;
        for (let j = start; j < end; j++) {
            const abs = Math.abs(channelData[j]);
            if (abs > maxAbs) maxAbs = abs;
        }
        peaks[i] = maxAbs;
    }

    return peaks;
}

/**
 * Convert BBC waveform-data v2 Int8 min/max pairs to a Float32Array of [0, 1] amplitudes,
 * downsampling to outputBuckets when the payload is larger.
 *
 * @param {Int8Array} rawData - Interleaved [min, max, min, max, ...] Int8 values
 * @param {number} channels - Number of audio channels in the BBC data
 * @param {number} outputBuckets - Desired number of output peaks; must be <= numBuckets
 * @returns {Float32Array}
 */
function convertBBCPeaks(rawData, channels, outputBuckets) {
    const stride = 2 * channels;
    const numBuckets = Math.floor(rawData.length / stride);
    const peaks = new Float32Array(outputBuckets);

    if (outputBuckets === numBuckets) {
        // Fast path: no downsampling needed. Merge all channels per frame via max-abs.
        for (let i = 0; i < numBuckets; i++) {
            const frameOffset = i * stride;
            let bucketMax = 0;
            for (let c = 0; c < channels; c++) {
                const min = rawData[frameOffset + c * 2];
                const max = rawData[frameOffset + c * 2 + 1];
                const amplitude = Math.max(Math.abs(min), Math.abs(max));
                if (amplitude > bucketMax) bucketMax = amplitude;
            }
            peaks[i] = bucketMax / 128;
        }
    } else {
        // Downsample: for each output bucket find the max abs amplitude across its input buckets,
        // merged across all channels.
        // bucketsPerPeak is fractional, so we floor the boundaries and guard against a zero-width
        // window with Math.max(startBucket + 1, ...) in case floating-point rounding collapses them.
        // Math.min(numBuckets, ...) ensures we never read past the end of the data.
        const bucketsPerPeak = numBuckets / outputBuckets;
        for (let i = 0; i < outputBuckets; i++) {
            const startBucket = Math.floor(i * bucketsPerPeak);
            const endBucket = Math.min(numBuckets, Math.max(startBucket + 1, Math.floor((i + 1) * bucketsPerPeak)));
            let maxAbs = 0;
            for (let bucket = startBucket; bucket < endBucket; bucket++) {
                const frameOffset = bucket * stride;
                for (let c = 0; c < channels; c++) {
                    const min = rawData[frameOffset + c * 2];
                    const max = rawData[frameOffset + c * 2 + 1];
                    const amplitude = Math.max(Math.abs(min), Math.abs(max)) / 128;
                    if (amplitude > maxAbs) maxAbs = amplitude;
                }
            }
            peaks[i] = maxAbs;
        }
    }

    return peaks;
}

self.onmessage = function (event) {
    const data = event.data;

    if (data && data.bbcPeaksData) {
        // BBC peaks conversion path
        const rawData = new Int8Array(data.bbcPeaksData);
        const channels = data.channels || 1;
        const numBuckets = rawData.length / (2 * channels);
        const normalizedTarget = Number.isFinite(data.targetPeaks)
            ? Math.max(1, Math.ceil(data.targetPeaks))
            : numBuckets;
        const outputBuckets = Math.min(numBuckets, normalizedTarget);
        const peaks = convertBBCPeaks(rawData, channels, outputBuckets);
        self.postMessage({ peaksBuffer: peaks.buffer }, [peaks.buffer]);
        return;
    }

    // Channel data (raw audio samples) path
    const channelDataBuffer = data && data.channelData;
    const targetPeaks = (data && data.targetPeaks) || 8000;

    if (!channelDataBuffer) {
        throw new Error('Waveform peaks worker received no channel data');
    }

    const peaks = generatePeaksFromChannelData(new Float32Array(channelDataBuffer), targetPeaks);
    self.postMessage({ peaksBuffer: peaks.buffer }, [peaks.buffer]);
};
